import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Globe } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { LOCATIONS, areasForCity, AREA_LABELS } from "@/data/areas";
import { CLIENT_LOCATIONS } from "@/data/site";

const Locations = () => {
  useEffect(() => {
    document.title = "Areas We Serve — Click World Online";
  }, []);
  return (
    <>
      <PageHeader
        eyebrow="Areas We Serve"
        title={<>Where we've <span className="text-electric">served &amp; worked.</span></>}
        text="From our home base in Mansa, Punjab, to client cities across India and international markets — these are the places where Click World Online has genuinely served and worked with clients."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Areas We Serve" }]}
      />
      <section data-testid="locations-grid-section" className="bg-navy-deep">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <Reveal>
            <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-mist mb-3">
              Our service areas in India <span className="text-electric">&amp; client locations abroad.</span>
            </h2>
            <p className="text-slatec text-sm md:text-base max-w-2xl mb-12">
              Indian cities are full service areas — explore them below. International cities are places where we've
              worked with clients remotely.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {LOCATIONS.filter((l) => ["mansa", "bathinda", "chandigarh"].includes(l.slug)).map((l, i) => (
              <Reveal key={l.slug} delay={0.05 * i}>
                <Link
                  to={`/locations/${l.slug}`}
                  data-testid={`location-card-${l.slug}`}
                  className="group relative flex flex-col h-full bg-navy-light border border-white/10 rounded-3xl p-8 overflow-hidden hover:border-electric/40 transition-colors duration-300"
                >
                  <div className="absolute -top-16 -right-16 w-48 h-48 bg-brand/20 blur-[80px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />
                  <MapPin size={22} className="text-electric" />
                  <h2 className="mt-5 font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-mist group-hover:text-electric transition-colors duration-300">
                    {l.name}
                  </h2>
                  <p className="mt-2 text-slatec text-sm leading-relaxed flex-1">{l.tagline}</p>
                  {areasForCity(l.slug).length > 0 && (
                    <p className="mt-4 text-xs text-mist/40">
                      Popular here: {areasForCity(l.slug).map((a) => AREA_LABELS[a.service]).join(" · ")}
                    </p>
                  )}
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-mist/60 group-hover:text-electric group-hover:gap-3 transition-[gap,color] duration-300">
                    Explore {l.name} <ArrowUpRight size={16} />
                  </span>
                </Link>
              </Reveal>
            ))}
            {[
              { name: "Surrey", region: "British Columbia, Canada" },
              { name: "Brampton", region: "Ontario, Canada" },
              { name: "Casselberry", region: "Florida, USA" },
              { name: "Kathmandu", region: "Nepal" },
            ].map((c, i) => (
              <Reveal key={c.name} delay={0.05 * (i + 3)}>
                <div
                  data-testid={`location-card-intl-${c.name.toLowerCase()}`}
                  className="relative flex flex-col h-full bg-navy-light/60 border border-white/10 rounded-3xl p-8 overflow-hidden"
                >
                  <Globe size={22} className="text-electric" />
                  <h2 className="mt-5 font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-mist">
                    {c.name}
                  </h2>
                  <p className="mt-2 text-slatec text-sm leading-relaxed flex-1">{c.region}</p>
                  <span className="mt-5 text-xs uppercase tracking-[0.2em] text-mist/40">Clients served — remotely</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <p className="text-slatec text-sm">
              Also serving{" "}
              <Link to="/locations/mohali" data-testid="location-link-mohali" className="text-electric hover:underline">Mohali</Link>
              {" "}and businesses{" "}
              <Link to="/locations/punjab" data-testid="location-link-punjab" className="text-electric hover:underline">across Punjab</Link>.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="bg-navy-deep border-t border-white/5" data-testid="locations-clients-block">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <Reveal>
            <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-mist">Where we've worked with clients</h2>
            <p className="mt-3 text-slatec text-sm leading-relaxed max-w-2xl">
              Cities where our clients are located — across India and internationally. Most engagements here were
              delivered remotely; these are client locations, not office locations.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">India</h3>
                <div className="flex flex-wrap gap-2">
                  {CLIENT_LOCATIONS.india.map((c) => (
                    <span key={c} className="text-sm text-mist/80 border border-white/10 bg-white/5 rounded-full px-4 py-2">{c}</span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">International</h3>
                <div className="flex flex-wrap gap-2">
                  {CLIENT_LOCATIONS.international.map((c) => (
                    <span key={c} className="text-sm text-mist/80 border border-white/10 bg-white/5 rounded-full px-4 py-2">{c}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="bg-navy-deep border-t border-white/5">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-10">
          <Reveal>
            <p className="text-slatec text-sm md:text-base">
              Beyond Punjab — we serve businesses across India and work remotely with international clients in markets like Canada, the USA, the UK and Australia.{" "}
              <Link to="/service-areas" data-testid="locations-international-link" className="text-electric font-semibold hover:underline">
                Explore our full service areas →
              </Link>
            </p>
          </Reveal>
        </div>
      </section>
      <CTASection title="Your city not listed?" text="If you're in Punjab — or beyond — we likely already serve businesses like yours remotely. Ask us directly." />
    </>
  );
};

export default Locations;
